import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Clock,
} from 'lucide-react';

export default function StartupMetricsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [jumpWarning, setJumpWarning] = useState('');

  // [4] Update form state with localStorage autosave draft
  const DRAFT_KEY = 'innovher_metrics_draft_v1';
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      // ignore
    }
    return {
      period: '2026-10',
      periodLabel: 'Oct 2026',
      revenue: '120000',
      customers: '24',
      activeUsers: '310',
    };
  });

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const res = await portalApi.getStartupPortalMetrics();
      setData(res);
    } catch (err) {
      console.error('Failed to load startup metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  // Autosave draft to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    } catch (e) {
      // ignore
    }
  }, [formData]);

  // Check for big jump warning
  const handleInputChange = (field, value) => {
    // Numbers only for numeric fields
    if (field !== 'period' && field !== 'periodLabel') {
      const cleaned = value.replace(/[^0-9]/g, '');
      setFormData((prev) => ({ ...prev, [field]: cleaned }));

      // Big jump detection vs previous actuals
      const numVal = Number(cleaned) || 0;
      const prevRevenue = data?.summary?.revenueActualNum || 100000;
      if (field === 'revenue' && numVal > prevRevenue * 2) {
        setJumpWarning(
          `High variance: Revenue (₹${(numVal / 100000).toFixed(1)}L) is over 2x previous period. Please verify.`
        );
      } else if (field === 'revenue') {
        setJumpWarning('');
      }
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setSubmitError('');
      setSubmitSuccess('');

      await portalApi.submitStartupPortalMetrics({
        period: formData.period,
        periodLabel: formData.periodLabel,
        revenue: Number(formData.revenue) || 0,
        customers: Number(formData.customers) || 0,
        activeUsers: Number(formData.activeUsers) || 0,
      });

      setSubmitSuccess('Period update submitted successfully.');
      await loadMetrics();
      setTimeout(() => setSubmitSuccess(''), 4000);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit metrics update.');
    } finally {
      setSubmitting(false);
    }
  };

  const summary = data?.summary || {
    revenue: '₹1.2L',
    revenueTarget: 'Target ₹2L · 60%',
    revenuePct: 60,
    customers: 24,
    customersTarget: 'Target 40 · 60%',
    customersPct: 60,
    activeUsers: 310,
    activeUsersTarget: 'Target 500 · 62%',
    activeUsersPct: 62,
    burnRate: '₹0.9L',
    burnRateStatus: 'Within budget',
    isWithinBudget: true,
    pilotsDone: 4,
    pilotsDoneTarget: 5,
    pilotsDonePct: 80,
  };

  const chartData = data?.chartData || [
    { period: 'Jun', actual: 0.4, target: 0.8 },
    { period: 'Jul', actual: 0.6, target: 1.0 },
    { period: 'Aug', actual: 0.8, target: 1.4 },
    { period: 'Sep', actual: 1.0, target: 1.7 },
    { period: 'Oct', actual: 1.2, target: 2.0 },
  ];

  const history = data?.history || [
    { periodLabel: 'October', submittedAt: '2026-10-26', status: 'Reviewed' },
    { periodLabel: 'September', submittedAt: '2026-10-03', status: 'Reviewed' },
    { periodLabel: 'August', submittedAt: '2026-09-02', status: 'Late' },
  ];

  // Helper for progress bar color
  const getProgressColor = (pct) => {
    if (pct < 50) return 'bg-rose-500';
    if (pct < 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  // Helper for KPI subtext color
  const getKpiSubtextColor = (pct) => {
    if (pct < 50) return 'text-rose-400';
    if (pct < 80) return 'text-amber-400';
    return 'text-emerald-400';
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">
            Growth & Metrics
          </h1>
          <p className="text-xs text-[#8E8E99] mt-0.5">
            Track monthly actuals vs mentor targets, revenue traction, and submission history.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadMetrics}
            className="p-2 rounded-xl border border-[#2C2C34] bg-[#131318] text-[#8E8E99] hover:text-white transition-colors"
            title="Refresh metrics"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ========================================================
          [1] KPI SUMMARY CARDS (Yellow dashed section in wireframe)
          Shows actuals vs targets for current period.
          Color: red under 50%, amber under 80%, green at target.
         ======================================================== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Monthly revenue */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-2">
          <div className="text-xs font-medium text-[#8E8E99]">Monthly revenue</div>
          <div className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">
            {summary.revenue}
          </div>
          <div
            className={`text-xs font-semibold ${getKpiSubtextColor(
              summary.revenuePct || 60
            )}`}
          >
            {summary.revenueTarget}
          </div>
        </div>

        {/* Card 2: Customers */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-2">
          <div className="text-xs font-medium text-[#8E8E99]">Customers</div>
          <div className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">
            {summary.customers}
          </div>
          <div
            className={`text-xs font-semibold ${getKpiSubtextColor(
              summary.customersPct || 60
            )}`}
          >
            {summary.customersTarget}
          </div>
        </div>

        {/* Card 3: Active users */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-2">
          <div className="text-xs font-medium text-[#8E8E99]">Active users</div>
          <div className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">
            {summary.activeUsers}
          </div>
          <div
            className={`text-xs font-semibold ${getKpiSubtextColor(
              summary.activeUsersPct || 62
            )}`}
          >
            {summary.activeUsersTarget}
          </div>
        </div>

        {/* Card 4: Burn rate */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-2">
          <div className="text-xs font-medium text-[#8E8E99]">Burn rate</div>
          <div className="text-2xl font-extrabold text-[#F2F2F4] tracking-tight">
            {summary.burnRate}
          </div>
          <div
            className={`text-xs font-semibold ${
              summary.isWithinBudget ? 'text-emerald-400' : 'text-rose-400'
            }`}
          >
            {summary.burnRateStatus}
          </div>
        </div>
      </div>

      {/* ========================================================
          MIDDLE ROW:
          [2] Trend chart (Left, Col 7) + [3] Progress bars (Right, Col 5)
         ======================================================== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* [2] TREND CHART: Monthly revenue vs target (₹ lakh) */}
        <div className="lg:col-span-7 bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-[#F2F2F4]">
              Monthly revenue vs target (₹ lakh)
            </h2>
            <p className="text-[11px] text-[#8E8E99] mt-0.5">
              Actual vs target by month. Hover shows exact numbers.
            </p>
          </div>

          {/* Recharts LineChart */}
          <div className="h-56 w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 20, left: -15, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#2C2C34"
                  vertical={false}
                />
                <XAxis
                  dataKey="period"
                  stroke="#8E8E99"
                  fontSize={11}
                  tickLine={false}
                />
                <YAxis
                  stroke="#8E8E99"
                  fontSize={11}
                  tickLine={false}
                  domain={[0, 2.5]}
                  ticks={[0, 0.5, 1.0, 1.5, 2.0, 2.5]}
                  tickFormatter={(v) => v.toFixed(1)}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0E0E12',
                    borderColor: '#2C2C34',
                    borderRadius: '12px',
                    color: '#FFF',
                    fontSize: '12px',
                  }}
                  formatter={(value, name) => [
                    `₹${value}L`,
                    name === 'actual' ? 'Actual' : 'Target',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="actual"
                  stroke="#D42B92"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#D42B92', stroke: '#131318', strokeWidth: 1.5 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="target"
                  name="target"
                  stroke="#8E8E99"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#8E8E99', stroke: '#131318', strokeWidth: 1.5 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Legend at bottom center */}
          <div className="flex items-center justify-center gap-6 text-xs text-[#8E8E99] pt-2 border-t border-[#2C2C34]/60">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#D42B92]" />
              <span className="font-medium text-[#F2F2F4]">Actual</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#8E8E99]" />
              <span className="font-medium text-[#8E8E99]">Target</span>
            </div>
          </div>
        </div>

        {/* [3] PROGRESS BARS: Progress to target */}
        <div className="lg:col-span-5 bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4 flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-[#F2F2F4]">Progress to target</h2>
            <p className="text-[11px] text-[#8E8E99] mt-0.5">
              One bar per KPI. Targets are set by Admin or Mentor.
            </p>
          </div>

          {/* Bars List */}
          <div className="space-y-4 py-2">
            {/* Revenue Bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-[#8E8E99]">Revenue</span>
                <span className="text-[#F2F2F4]">{summary.revenuePct || 60}%</span>
              </div>
              <div className="h-3 w-full rounded-full bg-[#0E0E12] border border-[#2C2C34] overflow-hidden p-0.5">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getProgressColor(
                    summary.revenuePct || 60
                  )}`}
                  style={{ width: `${Math.min(100, summary.revenuePct || 60)}%` }}
                />
              </div>
            </div>

            {/* Customers Bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-[#8E8E99]">Customers</span>
                <span className="text-[#F2F2F4]">{summary.customersPct || 60}%</span>
              </div>
              <div className="h-3 w-full rounded-full bg-[#0E0E12] border border-[#2C2C34] overflow-hidden p-0.5">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getProgressColor(
                    summary.customersPct || 60
                  )}`}
                  style={{ width: `${Math.min(100, summary.customersPct || 60)}%` }}
                />
              </div>
            </div>

            {/* Active users Bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-[#8E8E99]">Active users</span>
                <span className="text-[#F2F2F4]">{summary.activeUsersPct || 62}%</span>
              </div>
              <div className="h-3 w-full rounded-full bg-[#0E0E12] border border-[#2C2C34] overflow-hidden p-0.5">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getProgressColor(
                    summary.activeUsersPct || 62
                  )}`}
                  style={{ width: `${Math.min(100, summary.activeUsersPct || 62)}%` }}
                />
              </div>
            </div>

            {/* Pilots done Bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-[#8E8E99]">Pilots done</span>
                <span className="text-[#F2F2F4]">{summary.pilotsDonePct || 80}%</span>
              </div>
              <div className="h-3 w-full rounded-full bg-[#0E0E12] border border-[#2C2C34] overflow-hidden p-0.5">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getProgressColor(
                    summary.pilotsDonePct || 80
                  )}`}
                  style={{ width: `${Math.min(100, summary.pilotsDonePct || 80)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="text-[11px] text-[#6E6E78] pt-2 border-t border-[#2C2C34]/60">
            Green at target (&ge;80%), Amber (50-79%), Red (&lt;50%).
          </div>
        </div>
      </div>

      {/* ========================================================
          BOTTOM ROW:
          [4] Update form (Left, Col 7) + [5] Update history (Right, Col 5)
         ======================================================== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* [4] UPDATE FORM: Add this period's update */}
        <div className="lg:col-span-7 bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
            <h2 className="text-sm font-bold text-[#F2F2F4]">
              Add this period's update
            </h2>
            <span className="text-[11px] text-emerald-400 font-medium">
              Draft autosaves
            </span>
          </div>

          {submitSuccess && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{submitSuccess}</span>
            </div>
          )}

          {submitError && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">
              {submitError}
            </div>
          )}

          {jumpWarning && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{jumpWarning}</span>
            </div>
          )}

          <form onSubmit={handleSubmitUpdate} className="space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Period */}
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1.5">
                  Period
                </label>
                <input
                  type="text"
                  value={formData.periodLabel}
                  onChange={(e) => handleInputChange('periodLabel', e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92] transition-colors"
                  placeholder="e.g. Oct 2026"
                  required
                />
              </div>

              {/* Revenue */}
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1.5">
                  Revenue
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-2.5 text-[#8E8E99]">₹</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.revenue}
                    onChange={(e) => handleInputChange('revenue', e.target.value)}
                    className="w-full pl-8 pr-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92] transition-colors"
                    placeholder="1,20,000"
                    required
                  />
                </div>
              </div>

              {/* Customers */}
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1.5">
                  Customers
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.customers}
                  onChange={(e) => handleInputChange('customers', e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92] transition-colors"
                  placeholder="24"
                  required
                />
              </div>

              {/* Active users */}
              <div>
                <label className="text-[#8E8E99] font-medium block mb-1.5">
                  Active users
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.activeUsers}
                  onChange={(e) => handleInputChange('activeUsers', e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92] transition-colors"
                  placeholder="310"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-end pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:brightness-110 text-white font-semibold text-xs transition-all shadow-md shadow-[#D42B92]/30 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit update'}
              </button>
            </div>
          </form>
        </div>

        {/* [5] UPDATE HISTORY: Past periods with review status */}
        <div className="lg:col-span-5 bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-[#2C2C34]/60">
            <h2 className="text-sm font-bold text-[#F2F2F4]">Update history</h2>
            <span className="text-[11px] text-[#8E8E99]">
              {history.length} {history.length === 1 ? 'period' : 'periods'}
            </span>
          </div>

          <div className="space-y-3">
            {history.map((item, idx) => {
              const label =
                item.periodLabel ||
                new Date(item.period || Date.now()).toLocaleString('en-US', {
                  month: 'long',
                });
              const dateStr = item.submittedAt
                ? `Submitted ${new Date(item.submittedAt).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                  })}`
                : 'Submitted';

              const isLate = item.isLate || item.status === 'Late';

              return (
                <div
                  key={idx}
                  className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] flex items-center justify-between gap-3 hover:border-[#3E3E48] transition-colors"
                >
                  <div>
                    <div className="text-xs font-bold text-[#F2F2F4]">{label}</div>
                    <div className="text-[11px] text-[#8E8E99] mt-0.5">{dateStr}</div>
                  </div>

                  {/* Status Pill Badge */}
                  <div>
                    {isLate ? (
                      <span className="px-3.5 py-1 rounded-full border border-amber-500/50 bg-amber-500/10 text-amber-400 text-xs font-semibold">
                        Late
                      </span>
                    ) : (
                      <span className="px-3.5 py-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-400 text-xs font-semibold">
                        Reviewed
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Edge cases clarification notice */}
          <div className="p-3 rounded-xl bg-[#08080A] border border-[#2C2C34]/60 text-[11px] text-[#8E8E99] space-y-1">
            <div className="flex items-center gap-1.5 text-[#F2F2F4] font-medium">
              <HelpCircle className="w-3.5 h-3.5 text-[#D42B92]" />
              <span>Policy notes</span>
            </div>
            <div>• Past periods with review status: late updates are marked but allowed.</div>
            <div>• Wrong number: edit allowed until reviewed, then ask mentor.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
